/**
 * OperationalHealthStrip — thin strip summarising operational state.
 * Patch 36.0 · Project Obsidian.
 *
 * Sits between the Executive Briefing and Immediate Actions to give
 * founders a one-glance answer to "is anything on fire right now?".
 *
 * All content is derived from canonical backend data:
 *   • Akinex operations_sentinel findings → attention items
 *   • Autopilot dashboard state             → department status text
 *
 * Never mutates state; never issues client-side inference beyond
 * filtering by module name.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Clock } from "lucide-react";

import { useAkinexBriefing, flattenBriefingFindings } from "@/entities/akinex";
import { useCurrentWorkspace } from "@/entities/workspace";
import { getAutopilotDashboard } from "@/features/autopilot/api";
import { Card } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

export function OperationalHealthStrip(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;

  const briefingQ = useAkinexBriefing(workspaceId);
  const autopilotQ = useQuery({
    queryKey: ["autopilot-dashboard", workspaceId ?? ""],
    queryFn: getAutopilotDashboard,
    refetchInterval: 60_000,
    retry: false,
    enabled: !!workspaceId,
  });

  const loading = briefingQ.isLoading || autopilotQ.isLoading;

  if (!workspaceId || loading) {
    return (
      <Card
        className="p-4 border-white/10 obsidian-card"
        data-testid="obsidian-operational-health"
        data-state="loading"
      >
        <Skeleton className="h-4 w-1/3" />
      </Card>
    );
  }

  const briefing = briefingQ.data;
  const findings = briefing ? flattenBriefingFindings(briefing) : [];
  const opsFindings = findings.filter(
    (f) => f.module === "operations_sentinel",
  );

  const failed = opsFindings.filter(
    (f) => f.severity === "critical" || f.severity === "high",
  ).length;
  const attention = opsFindings.length;

  let tone: "healthy" | "attention" | "alert" = "healthy";
  let icon = <CheckCircle2 className="h-4 w-4 text-emerald-400" aria-hidden />;
  let label = "Operations are stable.";
  let detail = autopilotQ.data
    ? `Department state: ${autopilotQ.data.state}.`
    : "Department is standing by.";

  if (failed > 0) {
    tone = "alert";
    icon = <AlertCircle className="h-4 w-4 text-red-400" aria-hidden />;
    label = `${failed} operational ${failed === 1 ? "alert" : "alerts"}.`;
    detail = "Requires your attention.";
  } else if (attention > 0) {
    tone = "attention";
    icon = <Clock className="h-4 w-4 text-amber-400" aria-hidden />;
    label = `${attention} operational ${attention === 1 ? "signal" : "signals"} in-flight.`;
    detail = "Not blocking, but worth a glance.";
  }

  return (
    <Card
      className="flex flex-wrap items-center gap-3 p-4 border-white/10 obsidian-card"
      data-testid="obsidian-operational-health"
      data-tone={tone}
      data-state="populated"
    >
      {icon}
      <div className="flex-1 min-w-0">
        <p
          className="text-sm text-[var(--md-ivory-0)]"
          data-testid="obsidian-operational-health-label"
        >
          {label}
        </p>
        <p
          className="text-[11px] text-[var(--md-fog)]"
          data-testid="obsidian-operational-health-detail"
        >
          {detail}
        </p>
      </div>
    </Card>
  );
}
